'use client';
import { useRef, ChangeEvent } from 'react';
import Icon from '@/components/elements/Icon';
import { Icons } from '@/components/elements/Icon/constants';
import { chains } from '@/presentation/context/web3';
import { normalizeChainSlug } from '@/domain/network';
import { useNetwork } from '@/presentation/context/network';
import { useRouter } from 'next/navigation';
import { Network } from 'nftx.js';

// Define the type for the icon map
type IconMapType = {
  [key: string]: {
    name: Icons;
    color: string;
  };
};

const NetworkSelect = () => {
  const { slug } = useNetwork();
  const { push } = useRouter();
  const selectRef = useRef<HTMLSelectElement>(null);

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    push(`/${e.target.value}`);
  };

  const handleClick = () => {
    if (selectRef?.current) {
      selectRef.current.classList.remove('opacity-0');
      selectRef.current.focus();
    }
  };

  const options = Object.values(chains);

  const iconMap: IconMapType = {
    eth: {
      name: Icons.ETH,
      color: 'text-[#6b8aff] bg-[#6b8aff] bg-opacity-30',
    },
    arbitrum: {
      name: Icons.ARBITRUM,
      color: 'text-blue-500 bg-blue-500 bg-opacity-30',
    },
    base: { name: Icons.BASE, color: 'text-[#ffffff] bg-[#0052FF]' },
    sepolia: {
      name: Icons.ETH,
      color: 'text-pink-500 bg-pink-500 bg-opacity-30',
    },
    // add other networks here
  };

  const selectedIcon = iconMap[slug] || iconMap['eth']; // Default to ETH if slug is not found

  return (
    <div className="relative inline-flex">
      <select
        ref={selectRef}
        className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
        value={slug}
        onChange={handleSelectChange}
      >
        {options.map((c) => (
          <option value={normalizeChainSlug(c.id)} key={c.network}>
            {c.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={`rounded-lg h-10 px-2 inline-flex items-center justify-between cursor-pointer text-sm font-normal text-gray-800 bg-gray-100 border border-gray-300`}
        onClick={handleClick}
      >
        <span className="flex items-center mr-2">
          <Icon
            name={selectedIcon.name}
            size={24}
            className={`p-0.5 rounded-full ${selectedIcon.color}`}
          />
        </span>
        <Icon name={Icons.CHEVRON_DOWN} size={16} className="text-gray-400" />
      </button>
    </div>
  );
};

export default NetworkSelect;
